/**
 * 生成类错误码 → 统一提示文案
 *
 * 内容风控类错误码（600014 / 600015 / 600016）的文案由产品指定，文本、图片、视频三类创作
 * 全部复用同一套标准描述，避免各入口自己造文案造成分叉。命中这些码时：
 *   1. 用 `message.warning` 提示对应文案；
 *   2. 调用方（画布节点 / 页面）回到「开始状态」，让用户修改内容后重新发起，而不是停在失败重试态。
 */
import i18n from '@/i18n/config';

/** 错误码 → i18n key */
const ERROR_CODE_MESSAGE_KEYS: Record<number, string> = {
  // 风控检测结果违规
  600014: 'canvas.messages.chatSensitiveContent',
  // 调用风控检测 api 失败
  600015: 'canvas.messages.chatServiceBusy',
  // 字数超出风控限制
  600016: 'canvas.messages.chatWordLimitExceeded',
  // 模型版本过低，已不再提供服务（同属「改配置后重发」，处理方式一致）
  400009: 'imageCreation.modelVersionLow',
};

/** 是否需要「回到开始状态」（改内容/换模型后重发）的错误码 */
const RECOVERABLE_CODES = new Set([600014, 600015, 600016, 400009]);

const normalizeCode = (code?: number | string | null): number | undefined => {
  if (code === undefined || code === null || code === '') return undefined;
  const num = Number(code);
  return Number.isFinite(num) ? num : undefined;
};

/**
 * 取错误码对应的标准提示文案；无对应文案返回 undefined
 * （调用方据此判断是否为「风控 / 需修改后重发」类错误）
 */
export const getErrorCodeMessage = (code?: number | string | null): string | undefined => {
  const num = normalizeCode(code);
  if (num === undefined) return undefined;
  const key = ERROR_CODE_MESSAGE_KEYS[num];
  return key ? i18n.t(key) : undefined;
};

/** 是否为内容风控类错误码（600014 / 600015 / 600016） */
export const isRiskControlCode = (code?: number | string | null): boolean => {
  const num = normalizeCode(code);
  return num !== undefined && RECOVERABLE_CODES.has(num);
};
